import { useI18n } from 'vue-i18n'
import type {
  ClubInvitationEventType,
  ClubInvitationListItem,
  ClubInvitationStatus
} from '../types/clubInvitation.types'

// Order used by the status filter
export const CLUB_INVITATION_STATUSES: ClubInvitationStatus[] = [
  'queued',
  'sent',
  'failed',
  'opened',
  'clicked',
  'applied',
  'accepted',
  'rejected'
]

const STATUS_BADGE_CLASSES: Record<ClubInvitationStatus, string> = {
  queued: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
  opened: 'bg-yellow-100 text-yellow-800',
  clicked: 'bg-purple-100 text-purple-700',
  applied: 'bg-green-100 text-green-700',
  accepted: 'bg-green-600 text-white',
  rejected: 'bg-orange-100 text-orange-800'
}

const EVENT_DOT_CLASSES: Record<ClubInvitationEventType, string> = {
  queued: 'bg-gray-400',
  sent: 'bg-blue-500',
  failed: 'bg-red-500',
  opened: 'bg-yellow-500',
  clicked: 'bg-purple-500',
  applied: 'bg-green-500',
  accepted: 'bg-green-700',
  rejected: 'bg-orange-500'
}

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * Labels, colors and date formatting shared by the invitation list and history modal
 */
export function useClubInvitationStatus() {
  const { t } = useI18n()

  const statusLabel = (item: Pick<ClubInvitationListItem, 'status' | 'applications_count'>): string => {
    if (item.status === 'applied' && item.applications_count > 1) {
      return t('clubInvitations.status.appliedCount', { count: item.applications_count })
    }
    return t(`clubInvitations.status.${item.status}`)
  }

  const statusBadgeClass = (status: ClubInvitationStatus): string =>
    STATUS_BADGE_CLASSES[status] ?? STATUS_BADGE_CLASSES.queued

  const eventLabel = (type: ClubInvitationEventType): string => t(`clubInvitations.events.${type}`)

  const eventDotClass = (type: ClubInvitationEventType): string => EVENT_DOT_CLASSES[type] ?? EVENT_DOT_CLASSES.queued

  // ISO → YYYY-MM-DD HH:MM (local time)
  const formatDateTime = (iso: string | null): string => {
    if (!iso) return '-'
    const date = new Date(iso)
    if (isNaN(date.getTime())) return '-'
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  // ISO → DD.MM (local time)
  const formatDayMonth = (iso: string | null): string => {
    if (!iso) return ''
    const date = new Date(iso)
    if (isNaN(date.getTime())) return ''
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`
  }

  return {
    statusLabel,
    statusBadgeClass,
    eventLabel,
    eventDotClass,
    formatDateTime,
    formatDayMonth
  }
}
